import { useState } from 'react';
import PropTypes from 'prop-types';
import { formatRiskScore, getDefenderPortalLink } from '@/utils/riskScoring';

/**
 * Incident Card Component for SOC Dashboard
 * Displays security incident information with expandable drill-down details
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Incident title
 * @param {number} props.riskScore - Risk score (0-100)
 * @param {Object} props.data - Incident data object
 * @param {string} props.incidentType - Type of incident for Defender portal link
 * @param {Object} props.trend - Trend data with direction and percentage
 * @param {boolean} props.expandable - Whether card can be expanded
 */
const IncidentCard = ({
  title,
  riskScore,
  data,
  incidentType,
  trend,
  expandable = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Format risk score with severity information
  const riskInfo = formatRiskScore(riskScore);

  /**
   * Toggles expanded state of the card
   */
  const toggleExpand = () => {
    if (expandable) {
      setIsExpanded(prev => !prev);
    }
  };

  /**
   * Renders trend indicator arrow
   */
  const renderTrendIndicator = () => {
    if (!trend || trend.direction === 'neutral') return null;

    const trendColor = trend.direction === 'up' ? '#dc3545' : '#28a745';
    const trendArrow = trend.direction === 'up' ? '↑' : '↓';

    return (
      <span className="trend-indicator" style={{ color: trendColor }}>
        {trendArrow} {trend.percentage}%
      </span>
    );
  };

  /**
   * Renders the main incident count or metric
   */
  const renderMainMetric = () => {
    const count = data?.count || data?.incidents || data?.alerts || 0;
    return (
      <div className="incident-metric">
        <span className="metric-value" style={{ color: riskInfo.color }}>
          {count}
        </span>
        {renderTrendIndicator()}
      </div>
    );
  };

  /**
   * Formats an object value for display in a user-friendly way
   */
  const formatDetailValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'object') {
      // Handle arrays
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'None';
      }
      // Handle objects - show key properties only
      return Object.keys(value).length > 0 ? 
        `${Object.keys(value).length} properties` : 
        'Empty';
    }
    return String(value);
  };

  /**
   * Renders expanded details section
   */
  const renderDetails = () => {
    if (!isExpanded || !data) return null;

    return (
      <div className="incident-details">
        {/* Render key-value pairs from data object */}
        {Object.entries(data).map(([key, value]) => {
          // Skip certain keys or null/undefined values (using strict equality)
          if (key === 'count' || key === 'incidents' || key === 'alerts' || value === null || value === undefined) {
            return null;
          }

          // Format the key for display (convert camelCase to Title Case)
          const formattedKey = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase());

          return (
            <div key={key} className="detail-row">
              <span className="detail-label">{formattedKey}:</span>
              <span className="detail-value">
                {formatDetailValue(value)}
              </span>
            </div>
          );
        })}

        {/* Link to Microsoft 365 Defender Portal */}
        <div className="detail-row portal-link-row">
          <a
            href={getDefenderPortalLink(incidentType)}
            target="_blank"
            rel="noopener noreferrer"
            className="defender-portal-link"
          >
            View in Microsoft 365 Defender Portal →
          </a>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`incident-card ${isExpanded ? 'expanded' : ''} ${expandable ? 'expandable' : ''}`}
      onClick={expandable ? toggleExpand : undefined}
      style={{ borderLeftColor: riskInfo.color }}
    >
      <div className="incident-card-header">
        <h4 className="incident-title">{title}</h4>
        {expandable && (
          <span className="expand-icon">{isExpanded ? '−' : '+'}</span>
        )}
      </div>

      <div className="incident-card-body">
        {renderMainMetric()}
        
        <div className="risk-score-badge" style={{ backgroundColor: riskInfo.color }}>
          {riskInfo.severity}
        </div>

        {renderDetails()}
      </div>
    </div>
  );
};

IncidentCard.propTypes = {
  title: PropTypes.string.isRequired,
  riskScore: PropTypes.number.isRequired,
  data: PropTypes.object,
  incidentType: PropTypes.string.isRequired,
  trend: PropTypes.shape({
    direction: PropTypes.oneOf(['up', 'down', 'neutral']),
    percentage: PropTypes.number
  }),
  expandable: PropTypes.bool
};

IncidentCard.defaultProps = {
  data: {},
  trend: null,
  expandable: true
};

export default IncidentCard;
